import { AsyncPipe, CommonModule, provideImgixLoader } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, ErrorHandler, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouterModule } from '@angular/router';

import { StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { metaReducers } from '@state-management/middleware';
import { rootReducer } from '@state-management/reducers';

import { AccessDeniedComponent } from '@common/access-denied/access-denied.component';
import { FullComponent } from '@layouts/full/full.component';
import { SharedModule } from '@shared-modules/shared.module';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HeaderComponent } from './common/header/header.component';
import { AgentSwitcherComponent } from './common/header/agent-switcher/agent-switcher.component';
import { SignupRequestsPopupComponent } from './common/signup-requests-popup/signup-requests-popup.component';
import { SidebarComponent } from './common/sidebar/sidebar.component';

// Services
import { ApiService } from '@services/api/api.service';
import { AuthService } from '@services/auth.service';
import { TokenService } from '@services/helpers/token.service';
import { SharedSubjectService } from '@services/share-service';
import { SharedDataService } from '@services/shared-data.service';
import { MessageService } from '@services/util/message.service';
import { WS } from '@services/util/WebSocket.service';

import { ServiceWorkerModule } from '@angular/service-worker';
import { AuthTokenInterceptor } from '@interceptors/auth-token.interceptor';
import { ConfigCacheInterceptor } from '@interceptors/config-cache.interceptor';
import { DeploymentInProgressInterceptor } from '@interceptors/deployment-in-progress.interceptor';
import { SensitiveActionMfaInterceptor } from '@interceptors/sensitive-action-mfa.interceptor';
import { CheckPlatformService } from '@services/helpers/check-platform.service';
import { isLocalDevServer } from '@services/helpers/stale-chunk-recovery';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';
import { FormHelper } from '@services/helpers/form-helper';
import { ToasterService } from '@services/util/toaster.service';
import { SilentServerErrorToastrService } from '@services/util/silent-server-error-toastr.service';
import { ToastrService } from 'ngx-toastr';
import { NgxPermissionsModule, NgxPermissionsService } from 'ngx-permissions';
import { environment } from 'src/environments/environment';
import { PreviewListingModule } from './common/listing-preview/preview-listing.module';
import { FeatureFlagModule } from './core/directive/feature-flag/feature-flag.module';
import { AppErrorHandler } from './core/services/helpers/app-error.handler';
import { LeadManagementService } from './core/services/lm-service/lead-management.service';
import { AddOfficeContactModule } from './modules/contact/add-office-contact/add-office-contact.module';
import { EditOfficeContactModule } from './modules/contact/edit-office-contact/edit-office-contact.module';
import { ContractFormModule } from './modules/contract-agreements/contract-form/contract-form.module';
import { AddLeadModule } from './modules/leads-management/add-leads/add-lead.module';
import { EditLeadModule } from './modules/leads-management/edit-lead-detail/edit-lead.module';
import { AddPropertyModule } from './modules/my-listings/properties/add-properties/add-property.module';
import { PropsRightBarModule } from './modules/my-listings/props-rightbar/props-rightbar.module';
import { EditEoiModule } from './modules/my-projects/edit-project/project-edit/eoi/edit-eoi/edit-eoi.module';
import { EditUnitModule } from './modules/my-projects/edit-project/project-edit/unit/edit-unit/edit-unit.module';
import { CreateTaskModule } from './modules/my-tasks/create-new-task/create-task.module';
import { EditTaskModule } from './modules/my-tasks/edit-task/edit-task.module';
import { EditOfficeStaffModule } from './modules/setting/manage-user-contacts/staff/edit-office-staff/edit-office-staff.module';
import { HelpLeadsUiModule } from './modules/help-system/help-leads-ui.module';
import { AiAssistantModule } from './modules/ai-assistant/ai-assistant.module';
import { ChatModule } from './modules/chat/chat.module';
import { OtpInputModule } from './modules/Auth/input-one-time-password/otp-input.module';
import { CommonSharedModule } from './shared-modules/common-shared.module';
import { PipeDirectiveModule } from './shared-modules/pipe-directive.module';
import { HeaderNotesComponent } from './common/header/header-notes/header-notes.component';
import { FeatureReleaseWindowComponent } from './common/feature-release/feature-release-window/feature-release-window.component';
import { ReleaseHistoryDrawerComponent } from './common/feature-release/release-history-drawer/release-history-drawer.component';

export function initializeApp(flagService: FeatureFlagService) {
  return () => flagService.loadFlags();
}

@NgModule({
  declarations: [
    AppComponent,
    HeaderComponent,
    AgentSwitcherComponent,
    SignupRequestsPopupComponent,
    FullComponent,
    SidebarComponent,
    AccessDeniedComponent,
  ],
  imports: [
    CommonModule,
    FormsModule,
    FeatureFlagModule,
    RouterModule,
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NgxPermissionsModule.forRoot(),
    SharedModule,
    OtpInputModule,
    HelpLeadsUiModule,
    AiAssistantModule,
    ChatModule,
    CommonSharedModule,
    PreviewListingModule,
    AddOfficeContactModule,
    EditOfficeContactModule,
    PropsRightBarModule,
    AddPropertyModule,
    ContractFormModule,
    CreateTaskModule,
    EditTaskModule,
    AddLeadModule,
    EditLeadModule,
    PipeDirectiveModule,
    HeaderNotesComponent,
    FeatureReleaseWindowComponent,
    ReleaseHistoryDrawerComponent,
    EditUnitModule,
    EditEoiModule,
    EditOfficeStaffModule,
    StoreModule.forRoot(rootReducer, { metaReducers }),
    // Devtools keeps a 25-step state history in memory — dev only, never in deployed builds.
    environment.production ? [] : StoreDevtoolsModule.instrument({ maxAge: 25 }),
    ServiceWorkerModule.register('ngsw-worker.js', {
      // Never register against a local `ng serve`. `serve:dev-deploy` / `serve:staging` /
      // `serve:live` build with `serviceWorker: true` + `outputHashing: 'all'` and an
      // `environment.production === true` file, so a real worker used to install on
      // localhost. Every rebuild re-hashes the chunks while that worker keeps serving its
      // cached manifest, so a not-yet-cached lazy route is intercepted and never resolves —
      // the router hangs mid-navigation with no error to recover from. Deployed builds are
      // unaffected: they are never served from localhost.
      enabled: environment.production && !isLocalDevServer(),
      // Register immediately so update checks start ASAP. The previous
      // 'registerWhenStable:30000' strategy could leave a stale worker
      // intercepting requests on returning users (causing pending APIs).
      registrationStrategy: 'registerImmediately',
    }),
  ],
  exports: [],
  providers: [
    // Runs before AuthTokenInterceptor so a cache hit short-circuits before any auth/refresh logic.
    { provide: HTTP_INTERCEPTORS, useClass: ConfigCacheInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthTokenInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SensitiveActionMfaInterceptor, multi: true },
    // Last, so its one retry re-sends the request exactly as the interceptors above prepared it.
    { provide: HTTP_INTERCEPTORS, useClass: DeploymentInProgressInterceptor, multi: true },
    // Drops error toasts while a 5xx is being delivered — see server-error-silence.ts.
    { provide: ToastrService, useClass: SilentServerErrorToastrService },
    { provide: APP_ID, useValue: 'serverApp' },
    { provide: ErrorHandler, useClass: AppErrorHandler },
    // { provide: APP_BASE_HREF, useValue: '/v16/' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [FeatureFlagService],
      multi: true,
    },
    NgxPermissionsService,
    CheckPlatformService,
    TokenService,
    AuthService,
    MessageService,
    ApiService,
    AsyncPipe,
    FormHelper,
    LeadManagementService,
    SharedDataService,
    SharedSubjectService,
    provideImgixLoader(`${environment.S3}/`),
    ToasterService,
    WS,
    // DrawingPluginService is deliberately not listed: it is `providedIn: 'root'` and only the lazy
    // live-drawing-editor module injects it, so Konva ships with that chunk instead of main.
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
